import { useMemo } from 'react';
import { Trash2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useSession } from '@/stores/session';
import { projectViewsHooks } from '../queries';
import type { Project, ProjectView } from '../types';

/** Saved views are created/applied from the Work Items toolbar; this tab lists
 * and removes them. */
export function ProjectViewsTab({ project }: { project: Project }) {
  const query = projectViewsHooks.useList({ project: project.id });
  const remove = projectViewsHooks.useRemove();
  // Guests read saved views but can't delete them (staff-only).
  const isGuest = useSession((s) => s.role) === 'guest';

  const views = useMemo(() => {
    const raw = query.data;
    return (Array.isArray(raw) ? raw : (raw?.results ?? [])) as ProjectView[];
  }, [query.data]);

  if (query.isPending) return <Skeleton className="h-40 w-full" />;

  return (
    <div className="space-y-3">
      {views.length === 0 ? (
        <div className="rounded-md border border-dashed p-10 text-center">
          <p className="font-medium">No saved views</p>
          <p className="text-muted-foreground text-sm">
            Save the current layout + filters from the Work Items toolbar (“Views → Save current view”).
          </p>
        </div>
      ) : (
        <ul className="space-y-2">
          {views.map((v) => (
            <li key={v.id} className="flex items-center gap-3 rounded-md border px-3 py-2">
              <span className="flex-1 truncate text-sm font-medium">{v.name}</span>
              <Badge variant="outline" className="capitalize">{v.layout}</Badge>
              {v.is_shared ? <Badge className="text-xs">shared</Badge> : null}
              {!isGuest ? (
                <Button
                  variant="ghost" size="icon"
                  className="text-muted-foreground hover:text-destructive"
                  onClick={() => remove.mutate(v.public_id)}
                  aria-label={`Delete ${v.name}`}
                >
                  <Trash2 className="size-4" />
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
